import { createFileRoute, Link } from "@tanstack/react-router";
import { getNews, NEWSROOM_DISCLOSURE } from "@/lib/news";
import { SITE_URL } from "@/lib/site";
import {
  NEWSROOM_ID,
  PORTRAIT_ALT,
  PORTRAIT_URL,
  breadcrumbs,
  itemList,
  ldScript,
  orgRef,
  personRef,
  seoMeta,
  websiteRef,
} from "@/lib/schema";

const TITLE = "Buddha KA News | Official Updates from KAfrequency";
const DESCRIPTION =
  "Official news, release announcements, creative frameworks, and project updates from Buddha KA and KAfrequency.";

export const Route = createFileRoute("/news/")({
  component: NewsIndex,
  head: () => ({
    ...seoMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: "/news",
      image: PORTRAIT_URL,
      imageAlt: PORTRAIT_ALT,
    }),
    scripts: [
      ldScript({
        "@context": "https://schema.org",
        "@type": "Blog",
        "@id": NEWSROOM_ID,
        name: "Official Newsroom — Buddha KA & KAfrequency",
        description: DESCRIPTION,
        url: `${SITE_URL}/news`,
        inLanguage: "en",
        isPartOf: websiteRef(),
        about: [personRef(), orgRef()],
        creator: personRef(),
        author: personRef(),
        publisher: orgRef(),
        mainEntity: itemList(
          getNews().map((n) => ({ path: `/news/${n.slug}`, name: n.title })),
        ),
      }),
      ldScript(breadcrumbs([["News", "/news"]])),
    ],
  }),
});


function NewsIndex() {
  const items = getNews();
  const [featured, ...rest] = items;
  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Official Newsroom</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl">Buddha KA News</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Official news, release announcements, creative frameworks, and project updates from Buddha KA and
          KAfrequency.
        </p>
        <p className="mt-4 max-w-2xl rounded-xl border hairline bg-obsidian/60 p-4 text-sm text-muted-foreground">
          {NEWSROOM_DISCLOSURE}
        </p>
        <nav aria-label="Related destinations" className="mt-6 flex flex-wrap gap-2 text-sm">
          <Link to="/press" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            Buddha KA press kit
          </Link>
          <Link to="/about" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            Buddha KA biography
          </Link>
          <Link to="/music" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            Music catalog
          </Link>
          <Link to="/activations" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            Daily Activations
          </Link>
          <Link to="/contact" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            Media inquiries
          </Link>
        </nav>
      </header>


      {featured && (
        <Link
          to="/news/$slug"
          params={{ slug: featured.slug }}
          className="group mb-12 block overflow-hidden rounded-2xl border hairline bg-charcoal transition-colors hover:border-gold"
        >
          <div className="grid gap-0 md:grid-cols-[1.1fr_1fr]">
            <img
              src={featured.image.url}
              alt={featured.image.alt}
              className="aspect-[16/10] w-full object-cover md:aspect-auto md:h-full"
              loading="eager"
              decoding="async"
            />
            <div className="flex flex-col justify-center p-6 sm:p-10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {featured.category} ·{" "}
                <time dateTime={featured.publishedAt}>
                  {new Date(featured.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                </time>
              </p>
              <h2 className="mt-3 font-serif text-2xl leading-tight text-ivory group-hover:text-gold-luminous sm:text-3xl">
                {featured.title}
              </h2>
              <p className="mt-3 text-muted-foreground">{featured.dek}</p>
              <span className="mt-6 inline-flex items-center gap-1 text-sm text-gold">Read the story →</span>
            </div>
          </div>
        </Link>
      )}

      <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {rest.map((n) => (
          <li key={n.slug}>
            <Link
              to="/news/$slug"
              params={{ slug: n.slug }}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border hairline bg-charcoal transition-colors hover:border-gold"
            >
              <img
                src={n.image.url}
                alt={n.image.alt}
                loading="lazy"
                decoding="async"
                className="aspect-[16/10] w-full object-cover"
              />
              <div className="flex flex-1 flex-col p-6">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  {n.category} ·{" "}
                  <time dateTime={n.publishedAt}>
                    {new Date(n.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                  </time>
                </p>
                <h3 className="mt-3 font-serif text-xl leading-tight text-ivory group-hover:text-gold-luminous">
                  {n.title}
                </h3>
                <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{n.dek}</p>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      <aside className="mt-16 rounded-2xl border hairline bg-obsidian/60 p-6 text-sm text-muted-foreground sm:p-8">
        Media inquiries and interview requests →{" "}
        <Link to="/contact" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">/contact</Link>{" "}
        · Full press kit →{" "}
        <Link to="/press" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">/press</Link>
      </aside>
    </div>
  );
}
